
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { seedAIFundamentalsContent } from '@/utils/seed-modules';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const ContentSeeder = () => {
  const [isSeeding, setIsSeeding] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [results, setResults] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [pathExists, setPathExists] = useState<boolean | null>(null);
  const [modulesCount, setModulesCount] = useState<number | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    // Check if current user is an admin
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        setIsCheckingAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc('is_admin', {
          user_id: user.id
        });

        if (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        } else {
          setIsAdmin(!!data);
        }
      } catch (error) {
        console.error('Unexpected error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setIsCheckingAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  const checkPathAndModules = async () => {
    setIsChecking(true);
    setDebugInfo(null);
    
    try {
      // Check for AI Fundamentals path
      const { data: pathData, error: pathError } = await supabase
        .from('learning_paths')
        .select('id, title')
        .eq('title', 'AI Fundamentals')
        .maybeSingle();
      
      if (pathError) {
        setDebugInfo(`Error checking path: ${pathError.message}`);
        setPathExists(false);
        return;
      } 
      
      if (!pathData) {
        setDebugInfo('AI Fundamentals path not found. It will be created when you click the seed button.');
        setPathExists(false);
        return;
      }
      
      setPathExists(true);
      setDebugInfo(`Found AI Fundamentals path with ID: ${pathData.id}`);
      
      // Check for associated modules
      const { data: moduleData, error: moduleError } = await supabase
        .from('learning_path_modules')
        .select('id, module_id')
        .eq('path_id', pathData.id);
      
      if (moduleError) {
        setDebugInfo((prev) => `${prev}\nError checking modules: ${moduleError.message}`);
        return;
      }
      
      setModulesCount(moduleData?.length || 0);
      setDebugInfo((prev) => `${prev}\nFound ${moduleData?.length || 0} modules associated with this path.`);
      
      if (moduleData?.length === 0) {
        setDebugInfo((prev) => `${prev}\nNo modules found. Please click the seed button to create them.`);
      }
    } catch (error) {
      console.error('Error in check process:', error);
      setDebugInfo(`Unexpected error while checking: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    // Check if AI Fundamentals path exists on component mount
    if (isAdmin) {
      checkPathAndModules();
    }
  }, [isAdmin]);

  const handleSeedContent = async () => {
    if (!isAdmin) {
      toast({
        title: "Permission denied",
        description: "You need admin privileges to seed content",
        variant: "destructive"
      });
      return;
    }

    setIsSeeding(true);
    setResults(null);
    
    try {
      // Now seed the content
      const result = await seedAIFundamentalsContent();
      
      if (result.success) {
        setResults('Successfully seeded AI Fundamentals content!');
        setDebugInfo(`Seeding completed with ${result.results?.length || 0} module operations. Please refresh the page and navigate to Learning Paths to see updated content.`);
        
        // Log detailed results for debugging
        console.log('Detailed seeding results:', result.results);
        
        toast({
          title: "Content seeded successfully",
          description: "AI Fundamentals modules have been created. Please refresh the page and navigate to Learning Paths.",
        });
        
        // Refresh the path and modules check
        await checkPathAndModules();
      } else {
        setResults(`Error: ${result.error?.message || 'Unknown error'}`);
        setDebugInfo(`Error details: ${JSON.stringify(result.error)}`);
        toast({
          title: "Error seeding content",
          description: result.error?.message || "An unknown error occurred",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error in seed process:', error);
      setResults(`Unexpected error: ${error instanceof Error ? error.message : String(error)}`);
      toast({
        title: "Error seeding content",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSeeding(false);
    }
  };

  if (isCheckingAdmin) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center p-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
            <p>Checking permissions...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center text-amber-600 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-md">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <p>Admin privileges required to access this feature.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Content Seeder</CardTitle>
        <CardDescription>
          Populate the AI Fundamentals learning path with module content
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          This will create or update modules for the AI Fundamentals learning path with sample content.
          Use this to quickly populate your learning platform with high-quality educational material.
        </p>
        
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm">
            <span className="font-medium">Path Status:</span> {
              pathExists === null ? 'Checking...' :
              pathExists ? '✅ AI Fundamentals path exists' : '❌ Path not found'
            }
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={checkPathAndModules}
            disabled={isChecking}
          >
            {isChecking ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh Status
          </Button>
        </div>
        
        {modulesCount !== null && (
          <div className="text-sm mb-4">
            <span className="font-medium">Modules:</span> {
              modulesCount > 0 ? 
              `✅ Found ${modulesCount} modules` : 
              '❌ No modules associated with this path'
            }
          </div>
        )}
        
        {debugInfo && (
          <div className="p-3 rounded-md text-sm mb-4 bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200 overflow-auto max-h-48">
            <strong>Debug Info:</strong>
            <pre className="whitespace-pre-wrap">{debugInfo}</pre>
          </div>
        )}
        
        {results && (
          <div className={`p-3 rounded-md text-sm mb-4 ${results.includes('Error') ? 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200' : 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200'}`}>
            {results}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col items-start gap-2">
        <Button 
          onClick={handleSeedContent} 
          disabled={isSeeding}
        >
          {isSeeding ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Seeding Content...
            </>
          ) : (
            'Seed AI Fundamentals Content'
          )}
        </Button>
        <p className="text-xs text-muted-foreground mt-2">
          After seeding completes, please refresh the page and navigate to Learning Paths to see the updated content.
        </p>
      </CardFooter>
    </Card>
  );
};
